import React, { useState, useEffect } from 'react';
import './scheduled-maintenance-form.scss';
import { Form } from '@ampol/design-system';
import { yupDateTimeTransformer } from 'utilities/dayjs-utilities';
import { createScheduledMaintenance, updateScheduledMaintenance } from 'actions/maintenanceActions';
import { useDispatch, useSelector } from 'react-redux';

import * as yup from 'yup';
import SuffixWrapper from 'components/suffix-wrapper/suffix-wrapper';
import DeprecatedIcon from '../../../../assets/svg-icons.jsx';
import dayjs from 'dayjs';
import { isMobile } from '../../../../utilities/usersDevice';
import Validation from 'constants/validation-messages';
import Regex from 'constants/regex';
import clsx from 'clsx';
import { trackCustomEvent } from 'utilities/app-insights';

/**
 * Add/Edit scheduled maintenance form in the modal
 * @param formId
 * @param isEdit
 * @param onStateChange to enable or disable the submit button
 */
const ScheduledMaintenanceForm = ({ formId, isEdit, onStateChange }) => {
  const dispatch = useDispatch();

  const scheduledMaintenance = useSelector((state) => state.scheduledMaintenanceStore.scheduledMaintenance);
  const scheduledMaintenanceLoaded = useSelector((state) => state.scheduledMaintenanceStore.scheduledMaintenanceLoaded);
  const loggedInUser = useSelector((state) => state.userStore.loggedInUser);
  const assignedAccountNumber = useSelector((state) => state.userStore.assignedAccountNumber);

  const defaultMessage =
    'The portal is currently unavailable for scheduled maintenance from DD.MM.YYYY HH:mm to DD.MM.YYYY HH:mm. If you have any questions, please contact the Customer Service Centre on 1800 000 0000';

  const [formValues, setFormValues] = useState({
    startDate: null,
    endDate: null,
    title: '',
    message: isEdit ? '' : defaultMessage,
  });

  const formSchema = yup.object().shape({
    title: yup
      .string()
      .required(Validation.TITLE_REQUIRED)
      .matches(
        Regex.SCHEDULED_MAINTENANCE_ALPHA_NUM_SPACE_SPECIAL,
        Validation.SCHEDULED_MAINTENANCE_ALPHA_NUM_SPACE_SPECIAL
      )
      .max(100, Validation.MAX_CHAR_ONE_HUNDRED),
    message: yup
      .string()
      .required(Validation.MESSAGE_REQUIRED)
      .matches(
        Regex.SCHEDULED_MAINTENANCE_ALPHA_NUM_SPACE_SPECIAL,
        Validation.SCHEDULED_MAINTENANCE_ALPHA_NUM_SPACE_SPECIAL
      )
      .max(300, Validation.MAX_CHAR_THREE_HUNDRED),
    startDate: yup.date().nullable().required(Validation.START_DATE_REQUIRED).transform(yupDateTimeTransformer),
    endDate: yup
      .date()
      .nullable()
      .required(Validation.END_DATE_REQUIRED)
      .transform(yupDateTimeTransformer)
      .min(yup.ref('startDate'), Validation.END_DATE_AFTER_START)
      .test('is-not-same-minute', Validation.END_DATE_AFTER_START, function (endDate) {
        const startDate = this.resolve(yup.ref('startDate'));
        if (startDate && endDate) {
          return !dayjs(endDate).isSame(startDate, 'minute');
        }
        return true;
      }),
  });

  const handleChange = (values, name) => {
    if (name === 'startDate' || name === 'endDate') {
      const dateRegex = /\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}|DD\.MM\.YYYY HH:mm/g;

      if ((values.message.match(dateRegex) || []).length === 2) {
        let matchCount = 0;
        const updatedMessage = defaultMessage.replace(dateRegex, (match) => {
          matchCount++;
          if (matchCount === 1) {
            return values.startDate ? dayjs(values.startDate, 'DD/MM/YYYY HH:mm').format('DD.MM.YYYY HH:mm') : match;
          } else if (matchCount === 2) {
            return values.endDate ? dayjs(values.endDate, 'DD/MM/YYYY HH:mm').format('DD.MM.YYYY HH:mm') : match;
          }
          return match;
        });

        setFormValues({
          ...values,
          message: updatedMessage,
        });
      }
    }
  };

  useEffect(() => {
    if (isEdit && scheduledMaintenanceLoaded && scheduledMaintenance) {
      setFormValues({
        ...scheduledMaintenance,
        startDate: dayjs(scheduledMaintenance.startDate).format('DD/MM/YYYY HH:mm'),
        endDate: dayjs(scheduledMaintenance.endDate).format('DD/MM/YYYY HH:mm'),
      });
    }
  }, [scheduledMaintenanceLoaded]);

  const onFormSubmit = (values) => {
    const request = {
      ...values,
      startDate: values?.startDate ? dayjs(values.startDate) : '',
      endDate: values?.endDate ? dayjs(values.endDate) : '',
    };

    if (isEdit) {
      dispatch(updateScheduledMaintenance(scheduledMaintenance?.id, request));
      trackCustomEvent(
        'Submit Update Scheduled Maintenance',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Save to submit Update Scheduled Maintenance.',
        { selectedScheduledMaintenance: scheduledMaintenance?.id }
      );
    } else {
      dispatch(createScheduledMaintenance(request));
      trackCustomEvent(
        'Submit Add Scheduled Maintenance',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Save to submit Add Scheduled Maintenance.'
      );
    }
  };

  return (
    <div className={clsx('scheduled-maintenance-form', { 'scheduled-maintenance-form--edit': isEdit })}>
      <Form
        id={formId}
        schema={formSchema}
        onSubmit={onFormSubmit}
        onStateChange={onStateChange}
        defaultValues={formValues}
        onChange={handleChange}
      >
        <div className='scheduled-maintenance-form__section'>
          <Form.Input label={'Title'} name={'title'} />
          <div className='scheduled-maintenance-form__split-col'>
            <Form.DatePicker
              label={'Start Date & Time'}
              name={'startDate'}
              placeholder={'DD/MM/YYYY HH:mm'}
              format={'DD/MM/YYYY HH:mm'}
              showTime
              inputSuffix={
                <SuffixWrapper>
                  <DeprecatedIcon iconName='calender' />
                </SuffixWrapper>
              }
              getPopupContainer={() => document.body}
            />
            <Form.DatePicker
              label={'End Date & Time'}
              name={'endDate'}
              placeholder={'DD/MM/YYYY HH:mm'}
              format={'DD/MM/YYYY HH:mm'}
              showTime
              inputSuffix={
                <SuffixWrapper>
                  <DeprecatedIcon iconName='calender' />
                </SuffixWrapper>
              }
              getPopupContainer={() => document.body}
            />
          </div>
          <Form.TextArea label={'Message'} name={'message'} rows={isMobile() ? 6 : 4} />
        </div>
      </Form>
    </div>
  );
};

export default ScheduledMaintenanceForm;
